import { useEffect, useId, useRef, useState, type FormEvent, type JSX } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  LEADERBOARD_KINDS,
  LEADERBOARD_LABELS,
  OFFICE_QUESTS,
  type AchievementDto,
  type LeaderboardKind,
  type OfficeQuest,
  type OfficeQuestTier,
} from '@duck/contracts';
import { api, ApiError } from '../../lib/api';
import { queryClient } from '../../lib/queryClient';

/**
 * M5 "Every Release Is a Raid Boss" (emotion: Pride). The guild surfaces — Quests, Achievements,
 * Leaderboards, and the "Ship a release" raid-boss moment — gathered behind HUD toggle buttons so the
 * world stays uncluttered. Everything here celebrates contribution POSITIVELY; nothing compares people
 * down, tracks them, or pressures them (Constitution Art. 1). Shipping a release fans a guild-wide
 * celebration out over the existing realtime CELEBRATE broadcast (see OfficePage.onShipRelease).
 */

/** Which slide-over panel (if any) is open. Only one at a time keeps the HUD calm. */
export type GuildPanelTab = 'quests' | 'achievements' | 'leaderboards' | null;

function errMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Could not do that just now — try again in a moment.';
}

/** A glassy slide-over container, dual-named (button label + dialog aria-label) and Esc-dismissable. */
export function Panel({
  title,
  icon,
  onClose,
  children,
}: {
  title: string;
  icon: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  // Focus management (WCAG 2.1.1 keyboard, 2.4.3 focus order). The HUD toggle button that opened us
  // sits OUTSIDE this section, so without moving focus in, the Escape handler below would never fire
  // and a screen reader would never announce the dialog. On open we remember the opener and pull focus
  // into the panel; on close we hand it back. This is deliberately NOT a full focus-trap — these are
  // non-blocking slide-overs beside a still-interactive world (see GuildPanels doc).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const section = sectionRef.current;
    // Prefer the close button (a clear, labelled landing spot), else the section itself.
    const target = section?.querySelector<HTMLElement>('[data-panel-close]') ?? section;
    target?.focus();
    return () => {
      // Restore focus to the opener if it's still in the document and focusable.
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  const motionProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 24 },
        transition: { duration: 0.22, ease: 'easeOut' as const },
      };
  return (
    <motion.section
      ref={sectionRef}
      {...motionProps}
      role="dialog"
      aria-label={title}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      // Sizing matches the Store (Wardrobe) panel so every main-menu surface reads as one system: a fixed
      // 24rem column, clamped to 92vw on narrow screens, capped to 78dvh so the body scrolls inside on
      // short viewports instead of running off the bottom. It mounts inside the top-right menu cluster
      // (right-aligned, below the burger), exactly where Store opens.
      className="pointer-events-auto flex max-h-[min(78dvh,42rem)] w-96 max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950/85 shadow-2xl outline-none backdrop-blur-xl"
    >
      <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white">
          <span aria-hidden="true">{icon}</span>
          {title}
        </h2>
        <button
          onClick={onClose}
          data-panel-close
          aria-label={`Close ${title}`}
          title="Close"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
        >
          ✕
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </motion.section>
  );
}

/**
 * Office Quests board (gamified-XP redesign). XP is NO LONGER a free button — it's earned by genuinely
 * being in the world WITH other people and doing real, cooperative office things. This board is the
 * "what do I do?" surface: it lists the frozen {@link OFFICE_QUESTS} catalog, grouped by tier, each with
 * its name, the GOAL (the real cooperative action that fires it server-side) and the reward. Nothing here
 * is a button to press — the server detects these from authoritative state (positions, seats, zones).
 * It teaches the loop; the warm "✨ {quest} · +{xp} XP" toast (QUEST_FIRED) is the in-world payoff.
 *
 * On-brand + accessible: the same disclosure/Panel pattern, dual-coded tier badges (icon + text, never
 * colour-only), reward chips with full text, and friendly framing — cooperation is the ceiling, never a
 * chore (Constitution Art. 1: never a surveillance/assignment surface).
 */

/** Tier display order (gentle → cooperative → the headline raid) + dual-coded label/icon. */
const TIER_ORDER: OfficeQuestTier[] = ['easy', 'standard', 'group', 'raid'];
const TIER_DISPLAY: Record<OfficeQuestTier, { label: string; icon: string; blurb: string }> = {
  easy: { label: 'Solo-friendly', icon: '🌱', blurb: 'Small, repeatable beats you can do on your own.' },
  standard: { label: 'Together', icon: '🤝', blurb: 'Little moments shared with a teammate.' },
  group: { label: 'Small team', icon: '👥', blurb: 'A few ducks, gathered with a shared goal.' },
  raid: { label: 'Guild raid', icon: '🔥', blurb: 'The headline — the whole room, together.' },
};

/** A single reward chip (dual-coded icon + text), shown only when the reward is non-zero. */
function RewardChip({ icon, value, label }: { icon: string; value: number; label: string }): JSX.Element | null {
  if (value <= 0) return null;
  return (
    <span
      aria-label={`+${value} ${label}`}
      title={`+${value} ${label}`}
      className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/80"
    >
      <span aria-hidden="true">{icon}</span>+{value}
    </span>
  );
}

/** One office-quest card: icon + name + goal ("how do I do this?") + the rewards it grants. */
function OfficeQuestCard({ quest }: { quest: OfficeQuest }): JSX.Element {
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="flex items-start gap-2.5">
        <span aria-hidden="true" className="text-2xl leading-none">
          {quest.icon}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-white">{quest.name}</div>
          {/* The GOAL — the real, cooperative action the SERVER detects. This is the teaching line. */}
          <div className="text-[12px] leading-snug text-white/60">{quest.goal}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <RewardChip icon="🎯" value={quest.xp} label="XP" />
        <RewardChip icon="🪙" value={quest.coins} label="coins" />
        <RewardChip icon="🏰" value={quest.influence} label="guild influence" />
      </div>
    </li>
  );
}

/**
 * The cooperative Office Quests board — the catalog, grouped + sorted by tier. Read-only (these aren't
 * pressable buttons; they're earned by playing): it shows players the real, cooperative ways to earn XP.
 */
function QuestsBody(): JSX.Element {
  // Group the frozen catalog by tier, in display order. Derived once per render off the constant.
  const byTier = TIER_ORDER.map((tier) => ({
    tier,
    quests: Object.values(OFFICE_QUESTS).filter((q) => q.tier === tier),
  })).filter((g) => g.quests.length > 0);

  return (
    <>
      <p className="mb-3 text-[12px] leading-snug text-white/55">
        XP is earned by genuinely being here together — there’s no “press to win” button. Do real office
        things with your teammates and a warm <span className="text-amber-200">✨ +XP</span> floats up.
      </p>
      <div className="flex flex-col gap-4">
        {byTier.map(({ tier, quests }) => {
          const meta = TIER_DISPLAY[tier];
          return (
            <section key={tier} aria-label={meta.label}>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/45">
                <span aria-hidden="true">{meta.icon}</span>
                {meta.label}
              </h3>
              <ul className="flex flex-col gap-2">
                {quests.map((q) => (
                  <OfficeQuestCard key={q.id} quest={q} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}

/** A single badge card. Dual-coded beyond colour: earned shows ✓ + "Earned"; in-progress "{n}/{m}". */
function AchievementCard({ a }: { a: AchievementDto }): JSX.Element {
  const pct = a.target > 0 ? Math.round((Math.min(a.progress, a.target) / a.target) * 100) : 0;
  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border p-3 ${
        a.earned ? 'border-amber-300/40 bg-amber-400/10' : 'border-white/10 bg-white/5'
      }`}
    >
      <div className="flex items-start gap-2">
        <span aria-hidden="true" className={`text-2xl ${a.earned ? '' : 'opacity-50 grayscale'}`}>
          {a.icon}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-white">{a.name}</div>
          <div className="text-[11px] leading-snug text-white/55">{a.description}</div>
        </div>
      </div>
      {a.earned ? (
        <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-300">
          <span aria-hidden="true">✓</span> Earned
        </div>
      ) : (
        <div>
          <div
            role="progressbar"
            aria-label={`${a.name} progress`}
            aria-valuemin={0}
            aria-valuemax={a.target}
            aria-valuenow={Math.min(a.progress, a.target)}
            className="h-1.5 w-full overflow-hidden rounded-full bg-white/10"
          >
            <div
              className="h-full rounded-full bg-amber-400/80 transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 text-right text-[11px] font-medium text-white/55">
            {Math.min(a.progress, a.target)}/{a.target}
          </div>
        </div>
      )}
    </div>
  );
}

/** Achievements / badges grid (read-only; server-attested positive history only). */
function AchievementsBody(): JSX.Element {
  const achievements = useQuery({ queryKey: ['achievements'], queryFn: api.myAchievements });
  const data = achievements.data ?? [];
  const earnedCount = data.filter((a) => a.earned).length;

  return achievements.isLoading ? (
    <p className="text-sm text-white/50">Polishing your badges…</p>
  ) : (
    <>
      <p className="mb-3 text-xs text-white/50">
        {earnedCount} of {data.length} earned — every one celebrates a kind of contribution.
      </p>
      <div className="grid grid-cols-1 gap-2">
        {data.map((a) => (
          <AchievementCard key={a.id} a={a} />
        ))}
      </div>
    </>
  );
}

/**
 * Quests & Achievements as ONE modal (UI redesign): a single calm surface with the cooperative quest
 * board on top and your earned badges below — both about positive contribution, gathered in one place.
 * Reached from the main (burger) menu.
 */
function QuestsAchievementsPanel({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <Panel title="Quests & Achievements" icon="🗺️" onClose={onClose}>
      <QuestsBody />
      <div className="my-4 border-t border-white/10" />
      <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/45">
        <span aria-hidden="true">🏅</span> Achievements
      </h3>
      <AchievementsBody />
    </Panel>
  );
}

/** A leaderboard entry as returned by the API (kept inline — no shared type import needed here). */
type BoardEntry = { rank: number; displayName: string; value: number; isSelf: boolean };

/**
 * The celebratory 🥇🥈🥉 podium for the top three of a board. Visually ordered 2nd · 1st · 3rd, with the
 * champion raised + brightest. Positive-only (Constitution Art. 1) — it only ever lifts people up.
 */
function Podium({ entries, unit }: { entries: BoardEntry[]; unit: string }): JSX.Element | null {
  const top = entries.slice(0, 3);
  if (top.length === 0) return null;
  // Display order puts the winner in the middle; each slot carries its medal + relative height.
  const order: { e: BoardEntry; medal: string; h: string; ring: string }[] = [];
  if (top[1]) order.push({ e: top[1], medal: '🥈', h: 'h-16', ring: 'border-slate-300/40 bg-slate-300/10' });
  if (top[0]) order.push({ e: top[0], medal: '🥇', h: 'h-20', ring: 'border-amber-300/50 bg-amber-400/15' });
  if (top[2]) order.push({ e: top[2], medal: '🥉', h: 'h-12', ring: 'border-orange-300/40 bg-orange-400/10' });

  return (
    <div className="mb-3 flex items-end justify-center gap-2" aria-label="Top three">
      {order.map(({ e, medal, h, ring }) => (
        <div key={`podium-${e.rank}`} className="flex min-w-0 flex-1 flex-col items-center gap-1">
          <span aria-hidden="true" className="text-2xl">
            {medal}
          </span>
          <span
            className={`max-w-full truncate text-center text-[11px] font-semibold ${
              e.isSelf ? 'text-amber-200' : 'text-white/85'
            }`}
            title={e.displayName}
          >
            {e.displayName}
            {e.isSelf && <span className="ml-1 text-[9px] uppercase text-amber-300/80">you</span>}
          </span>
          <div
            className={`flex w-full flex-col items-center justify-end rounded-t-lg border ${ring} ${h} px-1 pb-1`}
          >
            <span className="text-sm font-bold text-white">{e.value}</span>
            <span className="text-[9px] text-white/50">{unit}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** One leaderboard board (a kind). Positive-only ranks; the player's own row is highlighted warmly. */
function LeaderboardBoard({ kind }: { kind: LeaderboardKind }): JSX.Element {
  const board = useQuery({
    queryKey: ['leaderboard', kind],
    queryFn: () => api.leaderboard(kind),
  });
  const meta = LEADERBOARD_LABELS[kind];
  const data = board.data;

  const optIn = useMutation({
    mutationFn: (next: boolean) => api.setLeaderboardOptIn({ optIn: next }),
    onSuccess: () => {
      // The opt-in state is shared across every board, so refresh them all.
      LEADERBOARD_KINDS.forEach((k) =>
        queryClient.invalidateQueries({ queryKey: ['leaderboard', k] }),
      );
    },
  });

  return (
    <div className="mb-5 last:mb-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-white">
          <span aria-hidden="true">{meta.icon}</span>
          {meta.title}
        </h3>
      </div>

      {board.isLoading ? (
        <p className="text-sm text-white/50">Loading the board…</p>
      ) : (
        <>
          {(data?.entries?.length ?? 0) > 0 && (
            <Podium entries={data!.entries as BoardEntry[]} unit={meta.unit} />
          )}
          <ul className="flex flex-col gap-1">
            {(data?.entries ?? []).length === 0 ? (
              <li className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/50">
                No one’s on this board yet. Kindness fills it. 💛
              </li>
            ) : (
              data!.entries.map((e) => (
                <li
                  key={`${kind}-${e.rank}-${e.displayName}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                    e.isSelf
                      ? 'border-amber-300/50 bg-amber-400/15'
                      : 'border-white/10 bg-white/5'
                  }`}
                >
                  <span className="w-6 shrink-0 text-center text-sm font-bold text-white/70">
                    {e.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-white">
                    {e.displayName}
                    {e.isSelf && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase text-amber-200">
                        you
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-white/90">
                    {e.value}
                    <span className="ml-1 text-[10px] font-normal text-white/40">{meta.unit}</span>
                  </span>
                </li>
              ))
            )}
          </ul>

          {/* Opt-in is plainly a choice. Default off; opted-out shows a friendly, pressure-free state. */}
          <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2">
            <span className="text-[11px] text-white/55">
              {data?.optedIn
                ? 'You’re listed on the boards.'
                : 'You’re not listed (your choice).'}
            </span>
            <button
              onClick={() => optIn.mutate(!data?.optedIn)}
              disabled={optIn.isPending || board.isLoading}
              role="switch"
              aria-checked={data?.optedIn ?? false}
              aria-label="Show me on the leaderboards"
              title="Appearing on the boards is entirely your choice — toggle any time."
              className={`flex min-h-[2.25rem] shrink-0 items-center rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ${
                data?.optedIn
                  ? 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25'
                  : 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10'
              }`}
            >
              {data?.optedIn ? '✓ Shown' : 'Show me'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Leaderboards view — one celebratory, positive-only board per kind. */
function LeaderboardsPanel({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <Panel title="Leaderboards" icon="🏆" onClose={onClose}>
      <p className="mb-3 text-xs text-white/50">
        Only kindness and contribution rank here. There’s no “least active”, ever.
      </p>
      {LEADERBOARD_KINDS.map((kind) => (
        <LeaderboardBoard key={kind} kind={kind} />
      ))}
    </Panel>
  );
}

interface GuildPanelsProps {
  /** Which panel is open (null = none). Controlled by the HUD's toggle buttons. */
  tab: GuildPanelTab;
  onClose: () => void;
}

/** The slide-over host: renders whichever guild panel is open. */
export function GuildPanels({ tab, onClose }: GuildPanelsProps): JSX.Element {
  return (
    <AnimatePresence>
      {(tab === 'quests' || tab === 'achievements') && (
        <QuestsAchievementsPanel key="quests-achievements" onClose={onClose} />
      )}
      {tab === 'leaderboards' && <LeaderboardsPanel key="leaderboards" onClose={onClose} />}
    </AnimatePresence>
  );
}

/**
 * The "Ship a release" raid-boss control — a name input + button. On success the parent fires the
 * guild-wide CELEBRATE broadcast (so everyone gets the 🚀 confetti). A tiny inline form to keep the
 * moment one click + a name away.
 */
export function ShipReleaseControl({
  onShipped,
}: {
  onShipped: (name: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const inputId = useId();
  const reduceMotion = useReducedMotion();

  const ship = useMutation({
    mutationFn: (n: string) => api.shipRelease({ name: n }),
    onSuccess: (release) => {
      setName('');
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['releases'] });
      void queryClient.invalidateQueries({ queryKey: ['guild'] });
      void queryClient.invalidateQueries({ queryKey: ['achievements'] });
      onShipped(release.name);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || ship.isPending) return;
    ship.mutate(trimmed);
  };

  const formMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Ship a release — the raid boss. A shared win the whole guild celebrates together."
        className="rounded-xl border border-fuchsia-300/40 bg-fuchsia-400/15 px-3 py-2 text-sm font-medium text-fuchsia-100 transition hover:bg-fuchsia-400/25"
      >
        🚀 Ship a release
      </button>
      <AnimatePresence>
        {open && (
          <motion.form
            {...formMotion}
            onSubmit={onSubmit}
            onKeyDown={(e) => {
              // FIX 4 — this inline form lives INSIDE the Guild HUD menu (a disclosure with its own Esc
              // handler). A first Esc should dismiss only THIS form, not collapse the whole menu, so we
              // stopPropagation() to keep the Esc from bubbling to the HudMenu's handler. A SECOND Esc
              // (form now closed) reaches the menu and closes it — the expected layered dismissal.
              if (e.key === 'Escape') {
                e.stopPropagation();
                setOpen(false);
              }
            }}
            aria-label="Ship a release"
            className="absolute right-0 top-12 z-10 w-72 rounded-2xl border border-white/10 bg-slate-950/85 p-3 shadow-2xl backdrop-blur-xl"
          >
            <label htmlFor={inputId} className="mb-1.5 block text-xs font-medium text-white/60">
              What did the guild ship?
            </label>
            <input
              id={inputId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoFocus
              placeholder="e.g. v1.4 — Spatial voice"
              className="mb-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-400/20"
            />
            <button
              type="submit"
              disabled={ship.isPending || name.trim().length === 0}
              className="w-full rounded-xl bg-fuchsia-400 px-3 py-2 text-sm font-semibold text-slate-900 transition hover:bg-fuchsia-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {ship.isPending ? 'Shipping…' : '🚀 Ship it — celebrate together'}
            </button>
            {ship.isError && (
              <p className="mt-2 text-[11px] text-red-300">{errMessage(ship.error)}</p>
            )}
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
